import { useCallback, useEffect, useRef, useState } from 'react';
import { notifyChartSaveError } from '../utilities/chartSaveError';

type LatchState = 'idle' | 'inflight' | 'failed' | 'done';

interface UseInitChartSectionOptions {
  /** True while the section needs initialising, e.g. `!!chart && !chart.ABN_INFO`. */
  needed: boolean;
  /**
   * Issues the write. It MUST return a promise that rejects on failure: pass `mutateAsync`,
   * never `mutate`. The latch settles on this promise, because TanStack drops per-call
   * `onSuccess`/`onError` for an unmounted observer and keeps only the latest call's options.
   */
  write: () => Promise<unknown>;
  /** Runs once per failed write. Defaults to the shared chart-save toast. */
  onError?: (_error: unknown) => void;
  /** Never write again this mount, even after a success. For a condition a success does not clear. */
  once?: boolean;
}

/**
 * Initialises a chart section in the background, at most once per failure.
 *
 * Why this exists (CU-17tm466jq2e): Firestore applies a write to the LOCAL cache first, then
 * ROLLS IT BACK if the server rejects it. A `useEffect([chart])` that writes whenever a section
 * is missing sees the rollback, writes again, and loops for as long as the tab is open
 * (1484 writes in 5 s, measured on the emulator). The stable toast id and Sentry `Dedupe`
 * hide that loop; neither stops it.
 *
 * The latch:
 * - is set to `inflight` BEFORE the write is issued, so a rollback snapshot cannot re-trigger
 *   it, whichever arrives first, the rejection or the rollback;
 * - becomes `failed` on rejection, which is terminal for this mount: no automatic retry. A
 *   remount retries, as does `rearm()` from a deliberate user action. `failed` is also returned,
 *   so a page that renders only once its section exists can offer that action (`ChartInitError`)
 *   instead of an endless "Loading...";
 * - returns to `idle` on SUCCESS (unless `once`). A success has already made the section
 *   present locally, so this cannot loop; it lets a section that is later deleted (by
 *   staff, or by the page's own handler) be initialised again, as it was before this hook.
 *
 * ⚠️ A rejection that arrives after unmount still calls `onError`. That is deliberate: the
 * write failed, and the promise settles regardless of the component.
 */
export function useInitChartSection({
  needed,
  write,
  onError = notifyChartSaveError,
  once = false,
}: UseInitChartSectionOptions): { rearm: () => void; failed: boolean } {
  const latch = useRef<LatchState>('idle');
  // Latest callbacks, so the effect depends only on `needed` and never fires a stale write.
  const writeRef = useRef(write);
  const onErrorRef = useRef(onError);
  writeRef.current = write;
  onErrorRef.current = onError;
  // Bumped by rearm(): when `needed` is already true, nothing else would re-run the effect.
  const [rearmCount, setRearmCount] = useState(0);
  // Mirrors the `failed` latch as state, so a page can replace its "Loading..." with a way forward.
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!needed || latch.current !== 'idle') return;
    latch.current = 'inflight';
    // The executor runs synchronously, so a write that throws becomes a rejection.
    new Promise((resolve) => resolve(writeRef.current())).then(
      () => {
        latch.current = once ? 'done' : 'idle';
      },
      (error: unknown) => {
        latch.current = 'failed';
        setFailed(true);
        onErrorRef.current(error);
      },
    );
  }, [needed, rearmCount, once]);

  const rearm = useCallback(() => {
    if (latch.current === 'inflight') return;
    latch.current = 'idle';
    setFailed(false);
    setRearmCount((count) => count + 1);
  }, []);

  return { rearm, failed };
}
